import { graphqlApiWithPersisted } from './graphqlApi';

export enum NewsletterConsentScreen {
  ONBOARDING = 'ONBOARDING',
  SETTINGS = 'SETTINGS',
}

export enum NewsletterConsentState {
  PENDING = 'PENDING',
  SUBSCRIBED = 'SUBSCRIBED',
  UNSUBSCRIBED = 'UNSUBSCRIBED',
}

interface NewsletterConsent {
  displayName: string;
  listKey: string;
  state: NewsletterConsentState;
  consentText?: string;
  description?: string;
}

export interface NewsletterConsents {
  isOn: boolean;
  lists: NewsletterConsent[];
  privacyPolicyUrl?: string;
}

export interface NewsletterConsentUpdateItem {
  listKey: string;
  state: NewsletterConsentState.SUBSCRIBED | NewsletterConsentState.UNSUBSCRIBED;
}

interface UserNewsletterConsentUpdateArgs {
  items: NewsletterConsentUpdateItem[];
  screen: NewsletterConsentScreen;
}

interface MeNewsletterConsentsResponse {
  data: { meNewsletterConsents: NewsletterConsents };
}

interface UserNewsletterConsentUpdateResponse {
  data: { userNewsletterConsentUpdate: { payload: NewsletterConsents | null } };
}

const NEWSLETTER_CONSENTS_FIELDS = `
  isOn
  lists { consentText description displayName listKey state }
  privacyPolicyUrl
`;

const newsletterConsentsApi = graphqlApiWithPersisted.injectEndpoints({
  endpoints: (builder) => ({
    meNewsletterConsents: builder.query<NewsletterConsents, void>({
      query: () => ({
        url: '/graphql/webapp?meNewsletterConsents',
        method: 'POST',
        body: {
          query: `query MeNewsletterConsents {
            meNewsletterConsents {
              ${NEWSLETTER_CONSENTS_FIELDS}
            }
          }`,
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: MeNewsletterConsentsResponse) =>
        response.data.meNewsletterConsents,
    }),
    userNewsletterConsentUpdate: builder.mutation<
      NewsletterConsents | null,
      UserNewsletterConsentUpdateArgs
    >({
      query: ({ items, screen }) => ({
        url: '/graphql/webapp?userNewsletterConsentUpdate',
        method: 'POST',
        body: {
          query: `mutation UserNewsletterConsentUpdate($input: UserNewsletterConsentUpdateInput!) {
            userNewsletterConsentUpdate(input: $input) {
              payload {
                ${NEWSLETTER_CONSENTS_FIELDS}
              }
              error {
                ... on NewsletterDisabledError {
                  message
                }
                ... on NewsletterListKeyError {
                  message
                }
                ... on UnknownError {
                  message
                  kind
                }
                ... on UserNotFoundError {
                  message
                }
              }
            }
          }`,
          variables: { input: { items, screen } },
        },
      }),
      transformResponse: (response: UserNewsletterConsentUpdateResponse) =>
        response.data.userNewsletterConsentUpdate.payload,
    }),
  }),
});

export const { useMeNewsletterConsentsQuery, useUserNewsletterConsentUpdateMutation } =
  newsletterConsentsApi;
